import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { getEnvironment } from "../../config/env.js";
import { get } from "svelte/store";
import { userStore } from "../stores/user-store.js";
import type { LogContext, AuthLogContext } from "../api/types.js";
import * as newrelicHandler from "./newrelic-handler.js";

let appInsights: ApplicationInsights | null = null;

export async function initializeLogger() {
  try {
    // Initialize New Relic Browser Agent handler (no-op, agent is loaded via script tag)
    newrelicHandler.initializeNewRelic().catch((error) => {
      // Silently fail - New Relic Browser Agent is optional
    });

    const env = getEnvironment();
    if (env.applicationInsightsConnectionString) {
      appInsights = new ApplicationInsights({
        config: {
          connectionString: env.applicationInsightsConnectionString,
          // Reduce auto instrumentation to avoid invalid payloads causing 400s
          enableAutoRouteTracking: false,
          disableAjaxTracking: true,
          disableFetchTracking: true,
          // Avoid adding correlation headers / auto-tracking against AI ingestion endpoints
          correlationHeaderExcludedDomains: ["eastus2-0.in.applicationinsights.azure.com", "eastus2-3.in.applicationinsights.azure.com", "dc.services.visualstudio.com"],
          excludeRequestFromAutoTrackingPatterns: [/https:\/\/.*\.applicationinsights\.azure\.com\/v2\/track/i, /https:\/\/dc\.services\.visualstudio\.com\/v2\/track/i],
        },
      });
      appInsights.loadAppInsights();
      // Sanitize telemetry to prevent invalid/oversized payloads causing 400s
      appInsights.addTelemetryInitializer((envelope: any) => {
        try {
          const baseData = envelope?.baseData || envelope?.data?.baseData;
          if (baseData?.properties && typeof baseData.properties === "object") {
            baseData.properties = sanitizeProperties(baseData.properties);
          }
          // Clamp trace message length if present
          if (baseData?.message && typeof baseData.message === "string") {
            baseData.message = clampString(baseData.message, 4096);
          }
          // Ensure exception messages are not too large
          if (baseData?.exceptions && Array.isArray(baseData.exceptions)) {
            baseData.exceptions = baseData.exceptions.map((ex: any) => {
              if (ex && typeof ex.message === "string") {
                ex.message = clampString(ex.message, 4096);
              }
              return ex;
            });
          }
        } catch (_e) {
          // Best-effort sanitizer; never throw
        }
      });
      appInsights.trackPageView(); // Track initial page view
    } else {
      console.warn("Application Insights connection string not found in environment");
    }
  } catch (error) {
    console.warn("Failed to initialize Application Insights:", error instanceof Error ? error.message : "Unknown error");
  }
}

// Helper function to get current user context
function getUserContext(): LogContext {
  const store = get(userStore);
  return {
    userId: store?.user?.email || undefined,
    sessionId: store?.session_id || undefined,
    component: "webapp",
  };
}

// Enhanced logging functions with unified context
export function logInfo(message: string, operationName: string, context?: Partial<LogContext>, properties?: Record<string, any>) {
  const userContext = getUserContext();
  const fullContext = { ...userContext, ...context };

  if (appInsights) {
    // Set the context fields directly when available
    if (fullContext.userId) appInsights.context.user.id = fullContext.userId;
    if (fullContext.sessionId) appInsights.context.session.id = fullContext.sessionId;
    appInsights.context.telemetryTrace.name = "webapp: " + operationName;

    appInsights.trackTrace({
      message: clampString(message, 4096),
      severityLevel: 1, // Info
      properties: {
        ...sanitizeProperties(properties),
        ...sanitizeProperties(fullContext),
      },
    });
  }

  // Also log to New Relic (will silently fail in browser environments)
  newrelicHandler.logInfo(message, operationName, fullContext, properties);
}

export function logWarning(message: string, operationName: string, context?: Partial<LogContext>, properties?: Record<string, any>) {
  const userContext = getUserContext();
  const fullContext = { ...userContext, ...context };

  if (appInsights) {
    // Set the context fields directly when available
    if (fullContext.userId) appInsights.context.user.id = fullContext.userId;
    if (fullContext.sessionId) appInsights.context.session.id = fullContext.sessionId;
    appInsights.context.telemetryTrace.name = "webapp: " + operationName;

    appInsights.trackTrace({
      message: clampString(message, 4096),
      severityLevel: 2, // Warning
      properties: {
        ...sanitizeProperties(properties),
        ...sanitizeProperties(fullContext),
      },
    });
  }
  newrelicHandler.logWarning(message, operationName, fullContext, properties);
}

export function logError(error: Error, operationName: string, context?: Partial<LogContext>, properties?: Record<string, any>) {
  const userContext = getUserContext();
  const fullContext = { ...userContext, ...context };

  if (appInsights) {
    // Set the context fields directly when available
    if (fullContext.userId) appInsights.context.user.id = fullContext.userId;
    if (fullContext.sessionId) appInsights.context.session.id = fullContext.sessionId;
    appInsights.context.telemetryTrace.name = "webapp: " + operationName;

    appInsights.trackException({
      error,
      properties: {
        ...sanitizeProperties(properties),
        ...sanitizeProperties(fullContext),
      },
    });
  }
  newrelicHandler.logError(error, operationName, fullContext, properties);
}

export function logMetric(name: string, value: number, context?: Partial<LogContext>, properties?: Record<string, any>) {
  const userContext = getUserContext();
  const fullContext = { ...userContext, ...context };

  // Metric logging is handled by Application Insights, no console needed

  if (appInsights) {
    appInsights.trackMetric({
      name,
      average: value,
      properties: {
        ...sanitizeProperties(fullContext),
        ...sanitizeProperties(properties),
      },
    });
  }

  // Also log to New Relic (will silently fail in browser environments)
  newrelicHandler.logMetric(name, value, fullContext, properties);
}

/**
 * Track a page view in Application Insights
 */
export function trackPageView(name: string, context?: Partial<LogContext>, properties?: Record<string, any>) {
  const userContext = getUserContext();
  const fullContext = { ...userContext, ...context };

  // Page view logging is handled by Application Insights, no console needed

  if (appInsights) {
    try {
      appInsights.trackPageView({
        name,
        properties: {
          ...sanitizeProperties(properties),
          ...sanitizeProperties(fullContext),
        },
      });
    } catch (error) {
      // logging failure
    }
  }
  newrelicHandler.trackPageView(name, fullContext, properties);
}

/**
 * Track a custom event in Application Insights
 */
export function trackEvent(name: string, context?: Partial<LogContext>, properties?: Record<string, any>) {
  const userContext = getUserContext();
  const fullContext = { ...userContext, ...context };

  // Event logging is handled by Application Insights, no console needed

  if (appInsights) {
    try {
      appInsights.trackEvent({
        name,
        properties: {
          ...sanitizeProperties(properties),
          ...sanitizeProperties(fullContext),
        },
      });
    } catch (error) {
      console.error("Error tracking event:", error);
    }
  }
  newrelicHandler.trackEvent(name, fullContext, properties);
}

// Authentication-specific logging functions
export function logAuthSuccess(authMethod: string, context?: Partial<AuthLogContext>, properties?: Record<string, any>) {
  const authContext: AuthLogContext = {
    authMethod,
    authSuccess: true,
    component: "webapp",
    ...context,
  };

  logInfo("Authentication successful", "auth-handler", authContext, properties);
  trackEvent("auth_success", authContext, properties);
}

export function logAuthError(error: Error, authMethod: string, context?: Partial<AuthLogContext>, properties?: Record<string, any>) {
  const authContext: AuthLogContext = {
    authMethod,
    authSuccess: false,
    authError: error.message,
    component: "webapp",
    ...context,
  };

  logError(error, "auth-handler", authContext, properties);
  trackEvent("auth_error", authContext, properties);
}

export function logAuthAttempt(authMethod: string, context?: Partial<AuthLogContext>, properties?: Record<string, any>) {
  const authContext: AuthLogContext = {
    authMethod,
    component: "webapp",
    ...context,
  };

  logInfo("Authentication attempt", "auth-handler", authContext, properties);
  trackEvent("auth_attempt", authContext, properties);
}

export function logTokenExpiration(context?: Partial<AuthLogContext>, properties?: Record<string, any>) {
  const authContext: AuthLogContext = {
    component: "webapp",
    ...context,
  };

  logWarning("Token expired", "auth-handler", authContext, properties);
  trackEvent("token_expiration", authContext, properties);
}

export function logSignOut(context?: Partial<AuthLogContext>, properties?: Record<string, any>) {
  const authContext: AuthLogContext = {
    component: "webapp",
    ...context,
  };

  logInfo("User signed out", "auth-handler", authContext, properties);
  trackEvent("sign_out", authContext, properties);
}

// --- helpers ---

function clampString(value: string, maxLen: number): string {
  if (typeof value !== "string") return String(value ?? "");
  return value.length > maxLen ? value.slice(0, maxLen) : value;
}

function sanitizeProperties(input?: Record<string, any>): Record<string, any> | undefined {
  if (!input || typeof input !== "object") return input;
  const MAX_STRING = 4096;
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(input)) {
    if (v === undefined) continue;
    const key = String(k).slice(0, 150);
    const valType = typeof v;
    if (valType === "string") {
      out[key] = clampString(v as string, MAX_STRING);
    } else if (valType === "number" || valType === "boolean") {
      out[key] = v;
    } else if (v === null) {
      out[key] = null;
    } else {
      // Objects, arrays, functions, symbols -> stringify safely
      try {
        out[key] = clampString(JSON.stringify(v, safeReplacer(), 0), MAX_STRING);
      } catch (_e) {
        out[key] = "[unserializable]";
      }
    }
  }
  return out;
}

function safeReplacer() {
  const seen = new WeakSet();
  return (_key: string, value: any) => {
    if (typeof value === "object" && value !== null) {
      if (seen.has(value)) return "[circular]";
      seen.add(value);
    }
    if (typeof value === "function") return "[function]";
    if (typeof value === "symbol") return String(value);
    return value;
  };
}
